import { BaseFeature } from '../../core/base-feature.js';

/**
 * HangmanFeature - Frontend feature for Evil Hangman game
 *
 * Controls the desktop overlay game from the frontend.
 * Receives full game state including ALL candidate words (intel advantage).
 * Can sabotage the game and change difficulty in real-time.
 */
export class HangmanFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this.currentState = null;
        this.stateCallbacks = new Set();
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'hangman',
            name: 'Evil Hangman',
            version: 'v1.0',
            tier: 'external',
            capabilities: ['start', 'sabotage', 'set_difficulty', 'get_state', 'end'],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Hangman] Initializing...');
        console.log('[Hangman] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId } = message;
        const command = cmd || action;

        switch (command) {
            case 'state_update':
                this.currentState = payload;
                this.notifyStateListeners(payload);
                break;

            case 'start_response':
            case 'guess_response':
            case 'sabotage_response':
            case 'set_difficulty_response':
            case 'get_state_response':
            case 'end_response': {
                const originalId = payload?.originalMessageId;
                if (originalId && this._pendingRequests.has(originalId)) {
                    const { resolve } = this._pendingRequests.get(originalId);
                    this._pendingRequests.delete(originalId);
                    resolve(payload);
                }
                break;
            }

            default:
                console.warn(`[Hangman] Unknown command: ${command}`);
        }
    }

    _sendRequest(action, payload = {}) {
        return new Promise((resolve, reject) => {
            const messageId = this.sendMessage(action, payload);
            const timeout = setTimeout(() => {
                this._pendingRequests.delete(messageId);
                reject(new Error(`Request '${action}' timed out`));
            }, 10000);

            this._pendingRequests.set(messageId, {
                resolve: (result) => {
                    clearTimeout(timeout);
                    resolve(result);
                },
                reject
            });
        });
    }

    startGame(options = {}) {
        return this._sendRequest('start', options);
    }

    guess(letter) {
        return this._sendRequest('guess', { letter });
    }

    sabotage(action) {
        return this._sendRequest('sabotage', { action });
    }

    setDifficulty(level) {
        return this._sendRequest('set_difficulty', { level });
    }

    getGameState() {
        return this._sendRequest('get_state');
    }

    endGame() {
        return this._sendRequest('end');
    }

    // Event subscriptions for React
    onStateUpdate(callback) {
        this.stateCallbacks.add(callback);
        return () => this.stateCallbacks.delete(callback);
    }

    notifyStateListeners(data) {
        for (const callback of this.stateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Hangman] Error in state callback:', error);
            }
        }
    }

    getCurrentState() {
        return this.currentState;
    }

    async cleanup() {
        console.log('[Hangman] Cleaning up...');
        this.currentState = null;
        this.stateCallbacks.clear();
        for (const [, { reject }] of this._pendingRequests) {
            reject(new Error('Feature cleanup'));
        }
        this._pendingRequests.clear();
    }
}
